import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { StatusPipeline } from "@/components/StatusPipeline";
import { ArrowLeft, UploadSimple, Check, Download, PaintBrush, Plus, LinkSimple, X, CloudArrowUp, ImageSquare, Receipt, Trash, Warning, Star, Archive, Globe } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ProjectMessages } from "@/components/ProjectMessages";
import { CustomChargeModal } from "@/components/CustomChargeModal";
import { AdminAdjustPriceModal } from "@/components/AdminAdjustPriceModal";
import { LaunchImageModal } from "@/components/LaunchImageModal";
import { SendBalanceReminderModal } from "@/components/SendBalanceReminderModal";
import { RecordPaymentModal } from "@/components/RecordPaymentModal";
import { SetQuoteModal } from "@/components/SetQuoteModal";

const STATUSES = ["submitted", "preproduction", "design", "review", "final", "delivered"];

export default function AdminProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [statusNote, setStatusNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [designers, setDesigners] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [linkModal, setLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [subModal, setSubModal] = useState(false);
  const [subForm, setSubForm] = useState({ title: "", description: "", service_type: "other", designer_id: "" });
  const [chargeOpen, setChargeOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [launchOpen, setLaunchOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [recordPayOpen, setRecordPayOpen] = useState(false);
  const [recordPayDefaultFull, setRecordPayDefaultFull] = useState(false);
  const [setQuoteOpen, setSetQuoteOpen] = useState(false);
  const [projectCharges, setProjectCharges] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const [p, d, ch] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get("/admin/designers"),
        api.get("/admin/custom-charges"),
      ]);
      setProject(p.data);
      setDesigners(d.data.filter((x) => x.active));
      setProjectCharges((ch.data || []).filter((c) => c.project_id === id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateStatus = async (status) => {
    try {
      await api.patch(`/projects/${id}/status`, { status, note: statusNote });
      setStatusNote("");
      toast.success(`Status updated to ${status}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const assign = async (designerId) => {
    setAssigning(true);
    try {
      await api.patch(`/projects/${id}/assign`, { designer_id: designerId || null });
      toast.success(designerId ? "Designer assigned" : "Designer unassigned");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAssigning(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/projects/${id}/deliverables`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Deliverable uploaded");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadFile = async (deliverable) => {
    if (deliverable.kind === "link" && deliverable.url) {
      window.open(deliverable.url, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const resp = await api.get(`/deliverables/${deliverable.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: deliverable.content_type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = deliverable.original_filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("Download failed");
    }
  };

  const downloadAllZip = async () => {
    try {
      const resp = await api.get(`/projects/${id}/download-all`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const a = document.createElement("a");
      const disposition = resp.headers["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(disposition);
      a.href = url;
      a.download = match?.[1] || "project-files.zip";
      a.click();
      window.URL.revokeObjectURL(url);
      const included = resp.headers["x-files-included"];
      if (included) toast.success(`Downloaded ${included} file${included === "1" ? "" : "s"} as ZIP`);
    } catch (e) {
      const detail = e?.response?.data;
      if (e?.response?.status === 404) toast.error("No files to download for this project");
      else toast.error(typeof detail === "string" ? detail : "ZIP download failed");
    }
  };

  const submitLink = async () => {
    if (!linkUrl.trim()) return;
    const url = linkUrl.trim().startsWith("http") ? linkUrl.trim() : `https://${linkUrl.trim()}`;
    try {
      await api.post(`/projects/${id}/deliverables/link`, { url, label: linkLabel || null });
      toast.success("Link added");
      setLinkModal(false);
      setLinkUrl("");
      setLinkLabel("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const confirmDelete = async () => {
    if (deleteConfirmText !== "DELETE") {
      toast.error("Type DELETE exactly to confirm");
      return;
    }
    setDeleting(true);
    try {
      const { data } = await api.delete(`/projects/${id}`);
      const parts = [];
      if (data.deliverables_deleted) parts.push(`${data.deliverables_deleted} file${data.deliverables_deleted === 1 ? "" : "s"}`);
      if (data.messages_deleted) parts.push(`${data.messages_deleted} message${data.messages_deleted === 1 ? "" : "s"}`);
      if (data.sub_projects_deleted) parts.push(`${data.sub_projects_deleted} sub-project${data.sub_projects_deleted === 1 ? "" : "s"}`);
      const detail = parts.length ? ` (also removed: ${parts.join(", ")})` : "";
      toast.success(`Deleted "${data.title}"${detail}`);
      navigate("/admin/projects", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete project");
      setDeleting(false);
    }
  };

  const toggleDriveMirror = async (deliverable) => {
    try {
      await api.patch(`/deliverables/${deliverable.id}/drive-mirror`, {
        mirrored: !deliverable.mirrored_to_drive,
      });
      toast.success(deliverable.mirrored_to_drive ? "Drive mark cleared" : "Marked as mirrored to Drive");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const deleteDeliverable = async (deliverable) => {
    const label = deliverable.label || deliverable.original_filename || "this file";
    if (!window.confirm(`Delete "${label}"? The client will no longer see it. This cannot be undone.`)) return;
    try {
      await api.delete(`/projects/${id}/deliverables/${deliverable.id}`);
      toast.success(`Deleted "${label}"`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const featureInPortfolio = async () => {
    try {
      const { data } = await api.post("/admin/portfolio", {
        title: project.title,
        description: project.description || "",
        service_type: project.service_type || "other",
        project_id: project.id,
        client_name: project.client_name || null,
        is_published: false,
      });
      toast.success("Added to portfolio (draft) — upload a cover image next");
      window.location.href = `/admin/portfolio`;
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const requestReview = async () => {
    if (!window.confirm(`Email ${project.client_email} a review request for "${project.title}"?`)) return;
    try {
      await api.post("/admin/reviews/request", {
        client_id: project.client_id,
        project_id: project.id,
      });
      toast.success(`Review request emailed to ${project.client_email}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
    }
  };

  const completeAndArchive = async () => {
    if (!window.confirm(`Mark "${project.title}" complete and move it to the archive? It will leave your active board but stay accessible from /admin/archive.`)) return;
    try {
      await api.patch(`/projects/${project.id}/archive`, { archived: true });
      toast.success("Archived — viewable in /admin/archive");
      navigate("/admin/projects");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not archive");
    }
  };

  const submitSubProject = async () => {
    try {
      const { data } = await api.post("/admin/projects/internal", {
        parent_project_id: id,
        title: subForm.title,
        description: subForm.description,
        service_type: subForm.service_type,
        designer_id: subForm.designer_id || null,
      });
      toast.success("Sub-project created");
      setSubModal(false);
      setSubForm({ title: "", description: "", service_type: "other", designer_id: "" });
      await load();
      // Jump to the new sub-project
      window.location.href = `/admin/projects/${data.id}`;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  if (!project) return <AdminLayout><div className="text-[#a3a3a3]">Loading…</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <Link to="/admin/projects" className="inline-flex items-center gap-2 text-sm text-[#a3a3a3] hover:text-white" data-testid="admin-back">
          <ArrowLeft size={16} /> All projects
        </Link>
        <div className="flex gap-2">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${id}/invoice.pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="download-invoice-btn"
            className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#262626] text-[#a3a3a3] hover:border-[#FF2D2D] hover:text-[#FF2D2D] flex items-center gap-2"
          >
            <Download size={12} /> Invoice PDF
          </a>
          <button
            onClick={() => { setDeleteConfirmText(""); setDeleteModal(true); }}
            data-testid="delete-project-btn"
            className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#262626] text-[#a3a3a3] hover:border-[#FF2D2D] hover:text-[#FF2D2D] flex items-center gap-2"
          >
            <Trash size={12} /> Delete project
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
        <div className="lg:col-span-8">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">{project.service_type}</div>
          <h1 className="font-display text-5xl md:text-6xl mt-2">{project.title}</h1>
          <div className="text-[#a3a3a3] mt-3 max-w-2xl">{project.description}</div>
          <div className="flex flex-wrap gap-4 mt-4 text-sm">
            <span><strong>Client:</strong> {project.client_name} ({project.client_email})</span>
            <span><strong>Created:</strong> {new Date(project.created_at).toLocaleDateString()}</span>
            <span><strong>Turnaround:</strong> {project.turnaround}</span>
            {project.target_date && <span><strong>Target:</strong> {project.target_date}</span>}
          </div>
        </div>
        <div className="lg:col-span-4 grid grid-cols-2 gap-px bg-[#262626] border border-[#262626]">
          <div className="bg-[#0a0a0a] p-4 min-w-0">
            <div className="text-[10px] text-[#a3a3a3] uppercase tracking-wider">Total</div>
            <div className="font-display text-lg sm:text-xl md:text-2xl mt-1 tabular-nums truncate">
              {project.payment_method === "points" ? `${project.total_points} pts` : formatUSD(project.total_usd)}
            </div>
          </div>
          <div className="bg-[#0a0a0a] p-4 min-w-0">
            <div className="text-[10px] text-[#a3a3a3] uppercase tracking-wider">Paid</div>
            <div className="font-display text-lg sm:text-xl md:text-2xl mt-1 text-[#FF2D2D] tabular-nums truncate">
              {project.payment_method === "points" ? `${project.points_used} pts` : formatUSD(project.paid_amount)}
            </div>
          </div>
          <div className="bg-[#0a0a0a] p-4 col-span-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] text-[#a3a3a3] uppercase tracking-wider">Payment</div>
                <div className="font-display text-lg mt-1 capitalize">{project.payment_status?.replace("_", " ")}</div>
              </div>
              {project.payment_method !== "points" && (
                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                  {project.payment_status === "awaiting_quote" && (
                    <button
                      type="button"
                      onClick={() => setSetQuoteOpen(true)}
                      data-testid="open-set-quote"
                      className="text-[10px] uppercase tracking-wider border border-[#FF2D2D] bg-[#FF2D2D] hover:bg-[#e02020] text-white px-3 py-1.5 font-bold transition-colors"
                      title="Send the client a written quote"
                    >
                      Send quote
                    </button>
                  )}
                  {project.payment_status === "quote_sent" && (
                    <span
                      className="text-[10px] uppercase tracking-wider border border-[#FFA94D] bg-[#FFA94D]/10 text-[#FFA94D] px-3 py-1.5 font-bold"
                      data-testid="quote-sent-badge"
                      title={`Waiting for ${project.client_name || 'client'} to approve`}
                    >
                      Quote sent · awaiting approval
                    </span>
                  )}
                  {project.quote_approval && (
                    <span
                      className="text-[10px] uppercase tracking-wider border border-[#22c55e] bg-[#22c55e]/10 text-[#22c55e] px-3 py-1.5 font-bold"
                      data-testid="quote-approved-badge"
                      title={`Approved ${new Date(project.quote_approval.at).toLocaleString()}`}
                    >
                      Quote approved ✓
                    </span>
                  )}
                  {(Number(project.total_usd || 0) - Number(project.paid_amount || 0)) > 0.01 && (
                    <>
                      <button
                        type="button"
                        onClick={() => { setRecordPayDefaultFull(true); setRecordPayOpen(true); }}
                        data-testid="open-mark-paid"
                        className="text-[10px] uppercase tracking-wider border border-[#FF2D2D]/40 bg-[#FF2D2D]/5 hover:bg-[#FF2D2D]/10 hover:border-[#FF2D2D] text-white px-2.5 py-1.5 transition-colors"
                        title="Mark the full outstanding balance as received + email receipt"
                      >
                        Mark paid
                      </button>
                      <button
                        type="button"
                        onClick={() => { setRecordPayDefaultFull(false); setRecordPayOpen(true); }}
                        data-testid="open-record-payment"
                        className="text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#FF2D2D] hover:text-white text-[#a3a3a3] px-2.5 py-1.5 transition-colors"
                        title="Record a partial offline payment (cash, check, wire)"
                      >
                        Record payment
                      </button>
                      <button
                        type="button"
                        onClick={() => setReminderOpen(true)}
                        data-testid="open-balance-reminder"
                        className="text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#FF2D2D] hover:text-white text-[#a3a3a3] px-2.5 py-1.5 transition-colors"
                        title="Email the client a balance-due reminder"
                      >
                        Send reminder
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => setAdjustOpen(true)}
                    data-testid="open-adjust-price"
                    className="text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#FF2D2D] hover:text-white text-[#a3a3a3] px-2.5 py-1.5 transition-colors"
                    title="Discount, credit, or waive remaining balance"
                  >
                    Adjust price
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Internal sub-project banner */}
      {project.is_internal && (
        <div className="mb-6 border border-[#FF2D2D] bg-[#FF2D2D]/5 p-3 flex items-center justify-between text-sm" data-testid="internal-banner">
          <span><strong>Internal sub-project</strong> {project.parent_project_title && <>· of <Link to={`/admin/projects/${project.parent_project_id}`} className="underline">{project.parent_project_title}</Link></>} — not visible to client</span>
        </div>
      )}

      {/* Feature in portfolio + Request review + Complete & archive (only for delivered, non-internal, non-archived) */}
      {project.status === "delivered" && !project.is_internal && (
        <div className="mb-6 space-y-3">
          {!project.archived && (
            <div className="border border-[#FF2D2D] bg-gradient-to-r from-[#FF2D2D]/10 to-transparent p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="complete-archive-block">
              <div className="flex items-center gap-3 min-w-0">
                <Archive size={20} className="text-[#FF2D2D] shrink-0" weight="fill" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Workflow</div>
                  <div className="text-sm">Project is delivered — move it out of your active board.</div>
                </div>
              </div>
              <button
                onClick={completeAndArchive}
                data-testid="complete-archive-btn"
                className="btn-primary px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2 shrink-0"
              >
                Mark complete & archive →
              </button>
            </div>
          )}
          {project.archived && (
            <div className="border border-[#262626] bg-[#0a0a0a] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="archived-banner">
              <div className="flex items-center gap-3 min-w-0">
                <Archive size={20} className="text-[#a3a3a3] shrink-0" />
                <div className="text-sm text-[#a3a3a3]">
                  This project is archived. It won't appear on the active board.
                </div>
              </div>
              <Link
                to="/admin/archive"
                className="btn-outline px-4 py-2 text-xs uppercase tracking-wider"
              >
                View archive →
              </Link>
            </div>
          )}
          <div className={`grid gap-3 ${project.service_type === "web" ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
            <div className="border border-[#262626] bg-[#0a0a0a] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="feature-portfolio-block">
              <div className="flex items-center gap-3">
                <ImageSquare size={20} className="text-[#FF2D2D]" weight="fill" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Public showcase</div>
                  <div className="text-sm">Add to your public portfolio.</div>
                </div>
              </div>
              <button
                onClick={featureInPortfolio}
                data-testid="feature-in-portfolio"
                className="btn-outline px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2"
              >
                Feature →
              </button>
            </div>
            <div className="border border-[#262626] bg-[#0a0a0a] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="request-review-block">
              <div className="flex items-center gap-3 min-w-0">
                <Star size={20} className="text-[#FF2D2D] shrink-0" weight="fill" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Social proof</div>
                  <div className="text-sm truncate">Email {project.client_name || "client"} a review request.</div>
                  {project.last_review_request_at && (
                    <div className="text-[10px] text-[#a3a3a3] mt-0.5">
                      Last sent {new Date(project.last_review_request_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={requestReview}
                data-testid="request-review-btn"
                className="btn-outline px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2 shrink-0"
              >
                Request review →
              </button>
            </div>
            {project.service_type === "web" && (
              <div className="border border-[#262626] bg-[#0a0a0a] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="launch-image-block">
                <div className="flex items-center gap-3 min-w-0">
                  <Globe size={20} className="text-[#FF2D2D] shrink-0" weight="fill" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Launch image</div>
                    <div className="text-sm">Auto-generate a "Vision brought to life" share graphic.</div>
                  </div>
                </div>
                <button
                  onClick={() => setLaunchOpen(true)}
                  data-testid="open-launch-image"
                  className="btn-outline px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2 shrink-0"
                >
                  Generate →
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <h2 className="font-display text-2xl mb-4 uppercase">Pipeline</h2>
      <StatusPipeline status={project.status} />

      {/* Designer assignment */}
      <div className="mt-6 border border-[#262626] bg-[#0a0a0a] p-5" data-testid="assign-block">
        <div className="flex items-center gap-2 mb-3">
          <PaintBrush size={16} className="text-[#FF2D2D]" weight="fill" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Assigned designer</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <select
            value={project.assigned_designer_id || ""}
            onChange={(e) => assign(e.target.value || null)}
            disabled={assigning}
            data-testid="assign-select"
            className="flex-1 bg-[#050505] border border-[#262626] px-4 py-3 text-sm focus:border-[#FF2D2D] outline-none"
          >
            <option value="">— Unassigned —</option>
            {designers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} ({d.active_projects} active)
              </option>
            ))}
          </select>
          {project.assigned_designer_name && (
            <div className="px-4 py-2 border border-[#FF2D2D] bg-[#FF2D2D]/5 text-sm font-semibold">
              Currently: {project.assigned_designer_name}
            </div>
          )}
        </div>
        {designers.length === 0 && (
          <div className="text-xs text-[#a3a3a3] mt-2">
            No active designers. <Link to="/admin/designers" className="text-[#FF2D2D] hover:underline">Add one →</Link>
          </div>
        )}
      </div>

      <div className="mt-6 border border-[#262626] bg-[#0a0a0a] p-5" data-testid="status-update-block">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D] mb-3">Update status</div>
        <input
          type="text"
          placeholder="Optional note for this update…"
          value={statusNote}
          onChange={(e) => setStatusNote(e.target.value)}
          data-testid="status-note"
          className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm focus:border-[#FF2D2D] outline-none mb-3"
        />
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => updateStatus(s)}
              data-testid={`set-status-${s}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                project.status === s
                  ? "border-[#FF2D2D] bg-[#FF2D2D] text-white"
                  : "border-[#262626] text-[#a3a3a3] hover:border-[#FF2D2D] hover:text-[#FF2D2D]"
              }`}
            >
              {project.status === s && <Check size={10} weight="bold" className="inline mr-1" />}
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Custom charges (admin only) */}
      <div className="mt-10 border border-[#262626] bg-[#0a0a0a] p-5" data-testid="charges-block">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Receipt size={14} className="text-[#FF2D2D]" weight="fill" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Custom charges</span>
          </div>
          <button
            onClick={() => setChargeOpen(true)}
            data-testid="new-custom-charge"
            className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2"
          >
            <Plus size={12} weight="bold" /> Send charge
          </button>
        </div>
        {projectCharges.length === 0 ? (
          <div className="text-xs text-[#a3a3a3]">No extra charges issued for this project. Use this for over-page-limit work, paid plugins, etc.</div>
        ) : (
          <div className="divide-y divide-[#262626]">
            {projectCharges.map((c) => (
              <div key={c.id} className="py-3 flex items-center justify-between gap-3" data-testid={`project-charge-${c.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{c.description}</div>
                  <div className="text-xs text-[#a3a3a3]">{new Date(c.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-display text-lg tabular-nums">{formatUSD(c.amount)}</span>
                  <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${
                    c.status === "paid" ? "border-[#22c55e]/40 text-[#22c55e]" :
                    c.status === "cancelled" ? "border-[#404040] text-[#a3a3a3]" :
                    "border-[#FF2D2D] text-[#FF2D2D]"
                  }`}>{c.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sub-projects list (admin only, top-level projects only) */}
      {!project.is_internal && (
        <div className="mt-10 border border-[#262626] bg-[#0a0a0a] p-5" data-testid="subprojects-block">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Internal sub-projects</span>
            <button
              onClick={() => setSubModal(true)}
              data-testid="new-subproject"
              className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2"
            >
              <Plus size={12} weight="bold" /> New sub-project
            </button>
          </div>
          {(project.sub_projects || []).length === 0 ? (
            <div className="text-xs text-[#a3a3a3]">No sub-projects yet. Spin one off and assign to another designer — clients won't see it.</div>
          ) : (
            <div className="divide-y divide-[#262626]">
              {project.sub_projects.map((sp) => (
                <Link
                  key={sp.id}
                  to={`/admin/projects/${sp.id}`}
                  data-testid={`sub-${sp.id}`}
                  className="flex items-center justify-between py-3 hover:bg-[#121212] -mx-2 px-2"
                >
                  <div>
                    <div className="font-semibold text-sm">{sp.title}</div>
                    <div className="text-xs text-[#a3a3a3]">
                      {sp.assigned_designer_name || "Unassigned"} · <span className="text-[#FF2D2D] capitalize">{sp.status}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Deliverables */}
      <div className="mt-12">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 className="font-display text-2xl uppercase">Deliverables</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setLinkModal(true)}
              data-testid="add-link-deliverable"
              className="btn-outline px-3 py-2 text-xs uppercase tracking-wider flex items-center gap-2"
            >
              <LinkSimple size={14} /> Add link
            </button>
            <button
              onClick={downloadAllZip}
              data-testid="admin-download-all-zip"
              disabled={!project.deliverables || project.deliverables.filter((d) => d.kind !== "link").length === 0}
              className="btn-outline px-3 py-2 text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Download every file on this project as a single ZIP"
            >
              <Archive size={14} /> Download all (.zip)
            </button>
            <label className="btn-primary px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 cursor-pointer">
              <UploadSimple size={14} weight="bold" />
              {uploading ? "Uploading…" : "Upload file"}
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                data-testid="upload-deliverable"
                onChange={onUpload}
                disabled={uploading}
              />
            </label>
          </div>
        </div>
        {!project.deliverables || project.deliverables.length === 0 ? (
          <div className="border border-[#262626] bg-[#0a0a0a] p-8 text-center text-[#a3a3a3]">
            No deliverables yet.
          </div>
        ) : (
          <div className="border border-[#262626] divide-y divide-[#262626]">
            {project.deliverables.map((d) => (
              <div key={d.id} className="bg-[#0a0a0a] p-4 flex items-center justify-between gap-4">
                <div className="min-w-0 flex items-center gap-3">
                  {d.kind === "link" ? <LinkSimple size={18} className="text-[#FF2D2D] shrink-0" /> : null}
                  <div className="min-w-0">
                    <div className="font-semibold truncate flex items-center gap-2">
                      <span className="truncate">{d.original_filename}</span>
                      {d.mirrored_to_drive && (
                        <span
                          data-testid={`drive-badge-${d.id}`}
                          className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#FF2D2D]/40 text-[#FF2D2D] flex items-center gap-1 shrink-0"
                          title={d.mirrored_to_drive_at ? `Mirrored ${new Date(d.mirrored_to_drive_at).toLocaleString()}${d.mirrored_to_drive_by ? ` by ${d.mirrored_to_drive_by}` : ""}` : "Mirrored to Drive"}
                        >
                          <Check size={10} weight="bold" /> Drive
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#a3a3a3]">
                      {d.kind === "link" ? d.url : `${(d.size / 1024).toFixed(1)} KB · ${d.content_type}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => toggleDriveMirror(d)}
                    data-testid={`toggle-drive-${d.id}`}
                    title={d.mirrored_to_drive ? "Clear Drive mirror flag" : "Mark as mirrored to Google Drive"}
                    className={`px-2.5 py-1.5 text-xs flex items-center gap-1.5 border transition-colors ${
                      d.mirrored_to_drive
                        ? "border-[#FF2D2D] bg-[#FF2D2D]/10 text-[#FF2D2D]"
                        : "border-[#262626] text-[#a3a3a3] hover:border-[#FF2D2D] hover:text-[#FF2D2D]"
                    }`}
                  >
                    <CloudArrowUp size={14} weight={d.mirrored_to_drive ? "fill" : "regular"} />
                    {d.mirrored_to_drive ? "Mirrored" : "Mark mirrored"}
                  </button>
                  <button
                    onClick={() => downloadFile(d)}
                    className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2"
                    data-testid={`admin-download-${d.id}`}
                  >
                    {d.kind === "link" ? <><LinkSimple size={14} /> Open</> : <><Download size={14} /> Download</>}
                  </button>
                  <button
                    onClick={() => deleteDeliverable(d)}
                    data-testid={`delete-deliverable-${d.id}`}
                    title="Delete this deliverable"
                    className="px-2 py-1.5 text-xs border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D]"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Activity */}
      <div className="mt-12">
        <h2 className="font-display text-2xl mb-4 uppercase">Activity</h2>
        <div className="space-y-3">
          {(project.status_history || []).map((h, i) => (
            <div key={i} className="border-l-2 border-[#FF2D2D] pl-4 py-1">
              <div className="text-xs text-[#a3a3a3]">{new Date(h.at).toLocaleString()} {h.by ? `· ${h.by}` : ""}</div>
              <div className="text-sm font-semibold capitalize">{h.status}</div>
              {h.note && <div className="text-sm text-[#a3a3a3]">{h.note}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div className="mt-12">
        <h2 className="font-display text-2xl mb-4 uppercase">Messages</h2>
        <ProjectMessages projectId={id} />
      </div>

      {/* Add Link modal */}
      {linkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="link-modal">
          <div className="border border-[#262626] bg-[#0a0a0a] w-full max-w-md p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Link deliverable</div>
                <h3 className="font-display text-2xl mt-1">Add a link</h3>
              </div>
              <button onClick={() => setLinkModal(false)} className="text-[#a3a3a3] hover:text-white"><X size={18} /></button>
            </div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">URL</label>
            <input
              type="text"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://yoursite.com"
              data-testid="link-url"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
            />
            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2">Label (optional)</label>
            <input
              type="text"
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              placeholder="Live preview · Staging · Final site"
              data-testid="link-label"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
            />
            <div className="flex gap-3 mt-6">
              <button onClick={submitLink} disabled={!linkUrl.trim()} data-testid="link-submit" className="btn-primary flex-1 px-4 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-50">Save link</button>
              <button onClick={() => setLinkModal(false)} className="btn-outline flex-1 px-4 py-3 text-sm uppercase tracking-wider">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-project modal */}
      {subModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="sub-modal">
          <div className="border border-[#262626] bg-[#0a0a0a] w-full max-w-lg p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Internal sub-project</div>
                <h3 className="font-display text-2xl mt-1">Spin off work</h3>
                <div className="text-xs text-[#a3a3a3] mt-1">Hidden from the client. Costs nothing. Goes to the designer you pick.</div>
              </div>
              <button onClick={() => setSubModal(false)} className="text-[#a3a3a3] hover:text-white"><X size={18} /></button>
            </div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Title</label>
            <input type="text" value={subForm.title} onChange={(e) => setSubForm({ ...subForm, title: e.target.value })} data-testid="sub-title" placeholder="e.g. Web banner for {parent}" className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none" />

            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2">Description</label>
            <textarea rows={3} value={subForm.description} onChange={(e) => setSubForm({ ...subForm, description: e.target.value })} data-testid="sub-description" className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none" />

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Type</label>
                <select value={subForm.service_type} onChange={(e) => setSubForm({ ...subForm, service_type: e.target.value })} data-testid="sub-type" className="w-full bg-[#050505] border border-[#262626] px-3 py-3 focus:border-[#FF2D2D] outline-none">
                  <option value="flyer">Flyer</option>
                  <option value="logo">Logo</option>
                  <option value="seal">Seal</option>
                  <option value="banner">Banner</option>
                  <option value="web">Web</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Designer</label>
                <select value={subForm.designer_id} onChange={(e) => setSubForm({ ...subForm, designer_id: e.target.value })} data-testid="sub-designer" className="w-full bg-[#050505] border border-[#262626] px-3 py-3 focus:border-[#FF2D2D] outline-none">
                  <option value="">— Unassigned —</option>
                  {designers.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={submitSubProject} disabled={!subForm.title || !subForm.description} data-testid="sub-submit" className="btn-primary flex-1 px-4 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-50">Create</button>
              <button onClick={() => setSubModal(false)} className="btn-outline flex-1 px-4 py-3 text-sm uppercase tracking-wider">Cancel</button>
            </div>
          </div>
        </div>
      )}
      {/* Custom charge modal */}
      <CustomChargeModal
        open={chargeOpen}
        client={project ? { id: project.client_id, name: project.client_name, email: project.client_email } : null}
        projectId={project?.id}
        defaultDescription={project ? `Additional work for "${project.title}"` : ""}
        onClose={() => setChargeOpen(false)}
        onCreated={() => load()}
      />

      {/* Adjust price modal */}
      <AdminAdjustPriceModal
        open={adjustOpen}
        project={project}
        onClose={() => setAdjustOpen(false)}
        onSaved={() => load()}
      />

      {/* Set quote modal (awaiting_quote → priced) */}
      <SetQuoteModal
        open={setQuoteOpen}
        project={project}
        onClose={() => setSetQuoteOpen(false)}
        onSaved={() => load()}
      />

      {/* Launch image modal */}
      <LaunchImageModal
        open={launchOpen}
        projectId={project?.id}
        onClose={() => setLaunchOpen(false)}
      />

      {/* Send balance reminder modal */}
      <SendBalanceReminderModal
        open={reminderOpen}
        project={project}
        onClose={() => setReminderOpen(false)}
        onSent={() => load()}
      />

      {/* Record payment modal */}
      <RecordPaymentModal
        open={recordPayOpen}
        project={project}
        defaultFullPayment={recordPayDefaultFull}
        onClose={() => setRecordPayOpen(false)}
        onSaved={() => load()}
      />

      {/* Delete project modal */}
      {deleteModal && project && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="delete-project-modal">
          <div className="border border-[#FF2D2D] bg-[#0a0a0a] w-full max-w-md p-6">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={20} weight="fill" className="text-[#FF2D2D]" />
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Permanent action</div>
            </div>
            <h3 className="font-display text-2xl">Delete this project?</h3>
            <div className="text-sm text-[#a3a3a3] mt-3 leading-relaxed">
              You're about to permanently delete <strong className="text-white">"{project.title}"</strong> and all of its data. This includes deliverables, messages, surveys, and any sub-projects. There is no undo.
            </div>
            <ul className="text-xs text-[#a3a3a3] mt-3 space-y-1 list-disc list-inside">
              <li>Custom charges linked to this project will remain (untouched amounts) but their project link will be cleared.</li>
              <li>Portfolio items sourced from this project will be detached but kept in the public showcase.</li>
              <li>Payments already collected stay in the books — only the project record disappears.</li>
            </ul>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-5 mb-2">Type <code className="text-[#FF2D2D]">DELETE</code> to confirm</label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              data-testid="delete-confirm-input"
              autoFocus
              placeholder="DELETE"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-2.5 text-sm focus:border-[#FF2D2D] outline-none font-mono uppercase"
            />
            <div className="flex gap-3 mt-5">
              <button
                onClick={confirmDelete}
                disabled={deleting || deleteConfirmText !== "DELETE"}
                data-testid="delete-confirm-btn"
                className="btn-primary flex-1 px-4 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-40"
              >
                {deleting ? "Deleting…" : "Delete forever →"}
              </button>
              <button
                onClick={() => setDeleteModal(false)}
                disabled={deleting}
                data-testid="delete-cancel-btn"
                className="btn-outline flex-1 px-4 py-3 text-sm uppercase tracking-wider"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
